/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Modal component
 *
 * @author Vladislav Oshkanov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.29.0
 * @since 1.0.0
 */

import React, { Fragment, ReactElement, RefObject, useEffect, useRef } from 'react';

import get from 'lodash/get';
import invoke from 'lodash/invoke';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { IRootReducer } from '../../../reducers/rootReducer';
import { KeyboardKeyCodeEnum } from '../../app/appConstants';
import RenderInBody from '../../app/hoc/RenderInBody';
import {
    makeSelectOnboardingIsActive,
    makeSelectOnboardingModalCurrentStep,
} from '../../onboarding/onboardingSelectors';
import { EOnboardingComponentsId } from '../../onboarding/types/onboardingTypes';
import { modalClose } from '../modalActions';
import {
    makeSelectorAllModals,
    makeSelectorModalCurrent,
    makeSelectorModalIsOpen,
    makeSelectorModalOnBackgroundCloseCallback,
    makeSelectorModalOnCloseCallback,
    makeSelectorModalOnCrossClickCallback,
    makeSelectorModalProps,
    makeSelectorModalRenderComponent,
} from '../modalSelectors';
import { IModalList, IModalParams, IModalRenderProps } from '../types/modalTypes';
import {
    CrossButtonStyled,
    FullScreenContainerStyled,
    ModalBackgroundStyled,
    ModalContainerStyled,
} from './styled/ModalStyled';

interface IConnectProps {
    isOnboardingActive: boolean;
    isOnboardingCurrentStep: EOnboardingComponentsId | undefined;
    currentModalId: string;
    isOpen: boolean;
    modalParams?: IModalParams;
    modals: IModalList;
    onBackgroundCloseCallback?: () => void;
    onCloseCallback?: () => void;
    onCrossClickCallback?: () => void;
    render: (props: IModalRenderProps) => ReactElement;
}

export interface IDispatchProps {
    closeModal: (payload: string) => void;
}

type TModal = IConnectProps & IDispatchProps;

const ModalWrapper = (props: TModal) => {
    const modalRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.isOpen && get(props.modalParams, 'autoFocus', true)) {
            invoke(modalRef, 'current.focus');
        }
        // hide window scroll if modal window is opened
        props.isOpen ? document.body.classList.add('show-modal') : document.body.classList.remove('show-modal');
    }, [props.currentModalId, props.isOpen]);

    const keyDownHandler = (event: React.KeyboardEvent, id: string) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ESCAPE) {
            event.preventDefault();
            event.stopPropagation();
            closeHandler(id);
        }
    };

    const backgroundClickHandler = (event: React.MouseEvent<HTMLDivElement>, id: string) => {
        if (props.modalParams?.isDisableBackgroundClickClose) {
            return;
        }

        const isClickOnBackground = (event.target as HTMLElement).getAttribute('data-name') === 'modal-background';

        if (isClickOnBackground && (props.modalParams?.closeable ?? true)) {
            if (props.onBackgroundCloseCallback) {
                props.closeModal(id);
                invoke(props, 'onBackgroundCloseCallback');
            } else {
                closeHandler(id);
            }
        }
    };

    const onCrossClick = (id: string) => {
        invoke(props, 'onCrossClickCallback');
        return closeHandler(id);
    };

    const closeHandler = (id: string) => {
        props.closeModal(id);
        invoke(props, 'onCloseCallback');
    };

    return (
        <RenderInBody>
            <Fragment>
                {
                    // render all visible modals
                    Object.keys(props.modals).map((id: string) => {
                        // get current modal
                        const modal = props.modals[id];
                        // if modal is active
                        if (modal.isOpen) {
                            return (
                                <FullScreenContainerStyled
                                    key={id}
                                    ref={id === props.currentModalId ? modalRef : null}
                                    style={{
                                        top: props.isOnboardingActive && props.isOnboardingCurrentStep ? '32px' : '0',
                                    }}
                                    visible={modal.isOpen}
                                    onKeyDown={(event: React.KeyboardEvent) => keyDownHandler(event, id)}
                                >
                                    <ModalBackgroundStyled
                                        data-name="modal-background"
                                        onClick={(event) => backgroundClickHandler(event, id)}
                                    >
                                        <ModalContainerStyled>
                                            {!props.modalParams?.isCustomCrossButton && (
                                                <CrossButtonStyled
                                                    alt="Close"
                                                    data-name="modal-close-button"
                                                    src={icons.cancelIcon}
                                                    onClick={() => onCrossClick(id)}
                                                />
                                            )}
                                            {modal.isOpen &&
                                                modal.render({
                                                    id,
                                                    onCloseModal: () => closeHandler(id),
                                                })}
                                        </ModalContainerStyled>
                                    </ModalBackgroundStyled>
                                </FullScreenContainerStyled>
                            );
                        }
                    })
                }
            </Fragment>
        </RenderInBody>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectProps>({
    currentModalId: makeSelectorModalCurrent(),
    isOnboardingActive: makeSelectOnboardingIsActive(),
    isOnboardingCurrentStep: makeSelectOnboardingModalCurrentStep(),
    isOpen: makeSelectorModalIsOpen(),
    modalParams: makeSelectorModalProps(),
    modals: makeSelectorAllModals(),
    onBackgroundCloseCallback: makeSelectorModalOnBackgroundCloseCallback(),
    onCloseCallback: makeSelectorModalOnCloseCallback(),
    onCrossClickCallback: makeSelectorModalOnCrossClickCallback(),
    render: makeSelectorModalRenderComponent(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    closeModal: (payload: string) => dispatch(modalClose(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModalWrapper);
